/// <amd-module name="VSS/Features/Directory/components/DirectoryContentView/DirectoryContentView" />
import { DirectoryGrid } from "../DirectoryGrid/DirectoryGrid";
import { DEFAULT_NAME_COLUMN } from "../DirectoryGrid/DirectoryGridConstants";
import { IDirectoryRow, IGridRow } from "../DirectoryGrid/DirectoryGrid.Props";
import { IDirectoryArtifact, IFavoriteData, ExceptionInfo } from "../../DirectoryContracts";
import { DirectoryFilterBar } from "../DirectoryFilterBar/DirectoryFilterBar";
import * as DirectoryResources from "VSS/Features/Directory/Resources";
import { DirectoryGroupKeys } from "DirectoryConstants";
import { IColumn } from "OfficeFabric/DetailsList";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as React from "react";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ZeroData } from "VSSUI/Components/ZeroData";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Page } from "VSSUI/Page";
import { IFilter } from "VSSUI/Utilities/Filter";
import { IHubViewState } from "VSSUI/Utilities/HubViewState";
import { IVssIconProps, VssIcon } from "VSSUI/VssIcon";
import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { ContentError } from "./ContentError";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { IVssContextualMenuItemProvider } from "VSSUI/VssContextualMenu";

/** Props provided to the connected component */
export interface IDirectoryContentViewProps<T extends IDirectoryArtifact> {
    /** The hub view state to use in this area */
    hubViewState: IHubViewState;

    ttiScenarioProps: { featureName: string; scenarioName: string };

    /** Command bar items to display in the header */
    commandBarItems?: IHeaderCommandBarItem[];

    /** View actions to display in the header */
    viewItems?: IHeaderCommandBarItem[];

    /** Any custom columns to render */
    customColumns?: IColumn[];

    /** Called per non-group row. Renders context menu. */
    getContextMenuItems?: (row: IDirectoryRow<T>) => IContextualMenuItem[];

    /** Adds additional items to context menu. Usually used for async retrieval of contributed menu items. */
    getContextMenuItemProviders?: (activatedItem: any) => IVssContextualMenuItemProvider[];

    /** Custom render function to render cells */
    onRenderCell?: (
        item: IDirectoryRow<T>,
        index: number,
        column: IColumn,
        defaultRenderer?: (item: IDirectoryRow<T>, index: number, column: IColumn) => JSX.Element
    ) => JSX.Element;

    /** Render a custom filter bar */
    onRenderFilterBar?: (filter: IFilter) => JSX.Element;

    /** Render area for user message bars */
    onRenderMessageArea?: () => JSX.Element | undefined;

    artifactIcon?: IVssIconProps;

    /** Name of the Directory Artifact */
    artifactName?: string;

    /** The favorites that have been loaded */
    favorites: { [key: string]: IFavoriteData };

    /** The directory rows to display */
    directoryRows: IGridRow<T>[];

    /** Are the rows in grouped mode? */
    isFilterActive: boolean;

    /** Is the filter bar visible */
    isFilterVisible: boolean;

    /** Is the all section loading? */
    isAllSectionLoading: boolean;

    isNavigating: boolean;

    readyToMarkTti: boolean;

    /** Any present exception info */
    exceptionInfo?: ExceptionInfo;

    /**
     * Performs navigation to provided URL.
     * Called when navigation should be performed such as when clicking an artifact title.
     * @param url The URL to navigate to.
     */
    navigateToUrl: (url: string) => any;

    /**
     * Called when a favorite star is clicked.
     * @param artifactId The ID of the artifact the star is associated with
     * @param artifactName The name of the artifact the star is associated with
     * @param favoriteId The ID of the favorite
     */
    toggleFavorite: (artifactId: string, artifactName: string, favoriteId?: string) => any;

    /** Called when the filter icon is clicked. */
    toggleFilter: () => any;

    /** Called when a row group.
     * @param rowGroupKey The row group key to expand/collapse
     * @param collapsed Whether the group should now be collapsed
     */
    toggleGroup: (rowGroupKey: string, collapsed: boolean) => any;

    /**
     * Called when the component mounts for the first time.
     */
    markTti: (featureName: string, scenarioName: string) => any;
}

/**
 * Component which displays the header, filter bar, and directory grid.
 * Represents the full content of the directory.
 * During error and filter scenarios this component may also display no-resoults-found and error experiences.
 */
export class DirectoryContentView<T extends IDirectoryArtifact> extends VssComponent<IDirectoryContentViewProps<T>> {
    public render(): JSX.Element {
        const { artifactName, onRenderMessageArea, isFilterActive, isFilterVisible, isNavigating, commandBarItems, viewItems } = this.props;

        if (isNavigating) {
            return (
                <div className="absolute-fill flex-column flex-center justify-center">
                    <Spinner size={SpinnerSize.large} label={DirectoryResources.LoadingMessage} />
                </div>
            );
        }

        return (
            <Page className="directory-page v-scroll-auto absolute-fill">
                {onRenderMessageArea && onRenderMessageArea()}
                <Header
                    title={artifactName}
                    titleSize={TitleSize.Large}
                    commandBarItems={[
                        {
                            id: "filter",
                            iconProps: { iconName: isFilterActive ? "FilterSolid" : "Filter" },
                            ariaLabel: DirectoryResources.FilterAriaLabel,
                            onActivate: this._onFilterToggled,
                            subtle: true
                        },
                        ...(viewItems || []),
                        ...(commandBarItems || [])
                    ]}
                />
                {isFilterVisible && this._renderFilterBar()}
                {this._renderContent()}
            </Page>
        );
    }

    public componentDidUpdate(prevProps: IDirectoryContentViewProps<T>) {
        if (!prevProps.readyToMarkTti && this.props.readyToMarkTti) {
            this.props.markTti(this.props.ttiScenarioProps.featureName, this.props.ttiScenarioProps.scenarioName);
        }
    }

    private _renderContent(): JSX.Element {
        const { exceptionInfo, isAllSectionLoading, isFilterActive, directoryRows } = this.props;

        if (exceptionInfo) {
            return this._renderExceptionInfo();
        } else if (isFilterActive && isAllSectionLoading) {
            return this._renderFullLoading();
        } else if (isFilterActive && directoryRows.length === 0) {
            return this._renderEmptyFilter();
        } else {
            return this._renderDirectoryGrid();
        }
    }

    private _renderExceptionInfo(): JSX.Element {
        return <ContentError exceptionsInfo={[this.props.exceptionInfo!]} />;
    }

    private _renderFullLoading(): JSX.Element {
        return <Spinner className="page-content-top" label={DirectoryResources.LoadingMessage} size={SpinnerSize.large} />;
    }

    private _renderEmptyFilter(): JSX.Element {
        return (
            <ZeroData
                className="page-content-top"
                imagePath={getAssetLocation(this.context.pageContext, "ms.vss-features/directory/content/no-results-found.svg")}
                imageAltText=""
                primaryText={DirectoryResources.NoResultsFound}
            />
        );
    }

    private _renderDirectoryGrid(): JSX.Element {
        const { customColumns, directoryRows, isFilterActive, onRenderCell, toggleGroup } = this.props;

        let columns;
        if (customColumns) {
            columns = [...customColumns];
        } else {
            columns = [DEFAULT_NAME_COLUMN];
        }

        return (
            <div className="directory-content page-content page-content-top v-scroll-auto">
                <DirectoryGrid
                    rows={directoryRows}
                    columns={columns}
                    isGrouped={!isFilterActive}
                    onGroupToggled={toggleGroup}
                    onItemClicked={this._onItemClicked}
                    onRenderCell={onRenderCell}
                    onRenderZeroDataRow={this._onRenderZeroDataRow}
                    getContextMenuItems={this.props.getContextMenuItems}
                    getContextMenuItemProviders={this.props.getContextMenuItemProviders}
                />
            </div>
        );
    }

    private _renderFilterBar = (): JSX.Element => {
        const {
            hubViewState: { filter },
            onRenderFilterBar
        } = this.props;

        if (onRenderFilterBar) {
            return onRenderFilterBar(filter);
        }

        return <DirectoryFilterBar filter={filter} />;
    };

    private _onFilterToggled = (): void => {
        const { toggleFilter } = this.props;

        toggleFilter();
    };

    private _onRenderZeroDataRow = (groupKey: string): JSX.Element | undefined => {
        if (groupKey === DirectoryGroupKeys.FavoriteGroupKey) {
            return (
                <FormatComponent format={DirectoryResources.FavoritesEmptyText}>
                    <VssIcon iconName="FavoriteStarFill" className="vss-FavoriteItem--starIcon" />
                    {this.props.artifactName ? this.props.artifactName.toLowerCase() : DirectoryResources.DefaultArtifactName}
                </FormatComponent>
            );
        }
    };

    private _onItemClicked = (item: IDirectoryRow<T>): void => {
        const url = item.data.url;
        this.props.navigateToUrl(url);
    };
}
